import React, { useEffect, useState } from 'react';
import { obtenerNombreEmpresaReal } from '../../../config/unificacion';
import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { useCorreosMarketingModel } from '../../../modelos/EmailMarkeeting/modeloCorreos';

const HistorialLogsCorreos = () => {
  const {
    obtenerLogsCorreos,
    logsCorreos,
    paginacionLogs,
    loadingLogs,
    errorLogs
  } = useCorreosMarketingModel();

  const { configuracion } = useConfiguracion();
  const nombreEmpresa = obtenerNombreEmpresaReal(configuracion.nombreEmpresa);
  
  const [paginaActual, setPaginaActual] = useState(1);
  const limitePorPagina = 20;

  useEffect(() => {
    if (nombreEmpresa) {
      obtenerLogsCorreos(nombreEmpresa, paginaActual, limitePorPagina);
    }
  }, [nombreEmpresa, paginaActual]);

  const obtenerBadgeEstado = (estado, motivo) => {
    const estilos = {
      abierto: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      enviado: 'bg-blue-50 text-blue-700 border-blue-100',
      fallido: 'bg-red-50 text-red-700 border-red-200',
    };
    return (
      <span 
        className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide cursor-help ${estilos[estado] || 'bg-gray-50'}`}
        title={estado === 'fallido' ? motivo : `Estado del correo: ${estado}`}
      >
        {estado}
      </span>
    );
  };

  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return '---';
    return new Date(fechaStr).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4 animate-fadeIn text-left text-sm">
      
      {/* CARD INFO HEADER */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex justify-between items-center">
        <div>
          <h3 className="text-base font-bold text-gray-900">Bitácora de Salida de Correos</h3>
          <p className="text-xs text-gray-400 mt-0.5">Historial universal de notificaciones de sistema y campañas salientes.</p>
        </div>
        <span className="text-xs font-mono bg-slate-100 border px-2.5 py-1 rounded-md text-gray-600 font-bold">
          Total registros: {paginacionLogs.total}
        </span>
      </div>

      {/* MANEJO DE ESTADOS */}
      {loadingLogs ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-900 mx-auto"></div>
          <p className="text-xs text-gray-400 mt-2 font-medium">Leyendo eventos en PostgreSQL...</p>
        </div>
      ) : errorLogs ? (
        <div className="bg-red-50 text-red-800 p-4 rounded-xl border border-red-100 text-xs font-semibold">
          ❌ Error al cargar bitácora: {errorLogs}
        </div>
      ) : logsCorreos.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-400 text-xs font-medium">
          No hay registros de correos salientes en la base de datos todavía.
        </div>
      ) : (
        <>
          {/* TABLA PRINCIPAL */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-gray-100 text-gray-500 text-[11px] font-bold uppercase tracking-wider">
                    <th className="py-3 px-4">Destinatario</th>
                    <th className="py-3 px-4">Origen / Evento</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-center">Enviado</th>
                    <th className="py-3 px-4 text-right">Apertura</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs text-gray-600">
                  {logsCorreos.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                      {/* DESTINATARIO */}
                      <td className="py-3 px-4">
                        <span className="font-bold text-gray-900 block truncate max-w-[180px]">{log.destinatario_nombre}</span>
                        <span className="text-[10px] text-gray-400 font-mono block mt-0.5">{log.destinatario_correo}</span>
                      </td>

                      {/* ORIGEN: ¿CAMPAÑA O AUTOMÁTICO? */}
                      <td className="py-3 px-4">
                        {log.campana_id ? (
                          <span className="text-blue-600 font-bold bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded text-[10px] tracking-wide inline-block" title="Enviado desde el módulo de marketing">
                            📢 CAMPAÑA: {log.campana_nombre || `#${log.campana_id}`}
                          </span>
                        ) : (
                          <span className="text-purple-600 font-bold bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded text-[10px] tracking-wide inline-block" title="Disparado automáticamente por el backend">
                            ⚙️ SISTEMA: {log.tipo_plantilla}
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400 ml-2 font-medium">({log.destinatario_rol})</span>
                      </td>

                      {/* BADGE ESTADO */}
                      <td className="py-3 px-4 text-center">
                        {obtenerBadgeEstado(log.estado, log.motivo_error)}
                      </td>

                      {/* FECHA ENVIADO */}
                      <td className="py-3 px-4 text-center font-mono text-[11px] text-gray-500">
                        {formatearFecha(log.created_at)}
                      </td>

                      {/* FECHA APERTURA REAL TRACKING */}
                      <td className="py-3 px-4 text-right font-mono text-[11px]">
                        {log.estado === 'abierto' ? (
                          <span className="text-emerald-600 font-semibold">👁️ {formatearFecha(log.fecha_abierto)}</span>
                        ) : log.estado === 'fallido' ? (
                          <span className="text-red-400 truncate max-w-[120px] block text-right ml-auto" title={log.motivo_error}>
                            Error de envío
                          </span>
                        ) : (
                          <span className="text-gray-300">No leído</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* BARRA DE PAGINACIÓN PREMIUM */}
            <div className="bg-slate-50 px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Mostrando página <span className="font-bold text-gray-700">{paginacionLogs.page}</span> de <span className="font-bold text-gray-700">{paginacionLogs.totalPages}</span>
              </div>
              <div className="flex space-x-1">
                <button
                  disabled={paginaActual === 1 || loadingLogs}
                  onClick={() => setPaginaActual(prev => prev - 1)}
                  className="px-2.5 py-1 text-xs font-bold border bg-white rounded shadow-sm text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  ◀ Anterior
                </button>
                <button
                  disabled={paginaActual === paginacionLogs.totalPages || loadingLogs}
                  onClick={() => setPaginaActual(prev => prev + 1)}
                  className="px-2.5 py-1 text-xs font-bold border bg-white rounded shadow-sm text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente ▶
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HistorialLogsCorreos;